import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { adminLogout } from '../utils/adminSession';
import { reportClientError } from '../utils/clientLogger';
import { AdminDashboardHeader } from '../components/admin/dashboard/AdminDashboardHeader';
import { AdminDashboardTabs } from '../components/admin/dashboard/AdminDashboardTabs';
import { AdminDashboardOverviewTab } from '../components/admin/dashboard/AdminDashboardOverviewTab';
import { AdminDashboardProjectsTab } from '../components/admin/dashboard/AdminDashboardProjectsTab';
import { AdminDashboardAnalyticsTab } from '../components/admin/dashboard/AdminDashboardAnalyticsTab';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [metrics, setMetrics] = useState(null);
  const [allProjects, setAllProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  const loadData = useCallback(async () => {
    try {
      const [metricsRes, projectsRes] = await Promise.all([
        axios.get(`${API}/admin/metrics`),
        axios.get(`${API}/projects`)
      ]);
      setMetrics(metricsRes.data);
      setAllProjects(projectsRes.data);
    } catch (error) {
      reportClientError('Error loading admin data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleLogout = async () => {
    try {
      await adminLogout();
    } catch (e) {
      reportClientError('Logout error:', e);
    } finally {
      navigate('/admin/login');
    }
  };

  const handleRefresh = async () => {
    setLoading(true);
    await loadData();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center" data-testid="admin-dashboard-loading-state">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-500/30 border-t-blue-500 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600" data-testid="admin-dashboard-loading-text">Carregando dados...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100" data-testid="admin-dashboard-page">
      <AdminDashboardHeader onRefresh={handleRefresh} onLogout={handleLogout} />

      <div className="max-w-7xl mx-auto px-4 py-8">
        <AdminDashboardTabs activeTab={activeTab} onChangeTab={setActiveTab} />

        {activeTab === 'overview' && (
          <AdminDashboardOverviewTab
            metrics={metrics}
            allProjects={allProjects}
            onOpen3DProject={(projectId) => navigate(`/render-3d/${projectId}`)}
          />
        )}

        {activeTab === 'projects' && (
          <AdminDashboardProjectsTab
            projects={allProjects}
            onOpen3DProject={(projectId) => navigate(`/render-3d/${projectId}`)}
            onOpenProject={(projectId) => navigate(`/projeto/${projectId}`)}
          />
        )}

        {activeTab === 'analytics' && <AdminDashboardAnalyticsTab allProjects={allProjects} />}
      </div>
    </div>
  );
};

export default AdminDashboard;
